import { useEffect, useState } from 'react';
import { useRange } from 'react-instantsearch';
import Slider from 'rc-slider';
import { useTranslation } from 'react-i18next';
import 'rc-slider/assets/index.css';

const getLocaleConfig = (locale) => ({
    'da': { currency: 'EUR', numberFormat: 'da-DK' },
    'en': { currency: 'EUR', numberFormat: 'en-GB' },
    'es': { currency: 'EUR', numberFormat: 'es-ES' }
}[locale] || { currency: 'EUR', numberFormat: 'en-GB' });

export function RangeSlider({ attribute, isRange = true }) {
    const { t, i18n } = useTranslation('common');
    const { start, range, canRefine, refine } = useRange({ attribute });
    const { min, max } = range;
    const [value, setValue] = useState(isRange ? [min, max] : min);

    const from = Math.max(min, Number.isFinite(start[0]) ? start[0] : min);
    const to = Math.min(max, Number.isFinite(start[1]) ? start[1] : max);

    const formatValue = (value, attribute) => {
        const { currency, numberFormat } = getLocaleConfig(i18n.language);

        if (attribute === 'price') {
            return new Intl.NumberFormat(numberFormat, {
                style: 'currency',
                currency: currency,
                maximumFractionDigits: 0,
            }).format(value);
        }

        const formattedNumber = new Intl.NumberFormat(numberFormat).format(value);

        if (attribute === 'construction_year_quarter') {
            // Extract the year and quarter from the value
            const year = Math.floor(value / 10); // Extract the first four digits as the year
            const quarter = value % 10; // Extract the last digit as the quarter

            // Format as "YYYY QX"
            return `${year} Q${quarter}`;
        }

        if (attribute === 'year_built') {
            return value;
        }

        if (attribute === 'plot_area' || attribute === 'living_area' || attribute === 'terrace_area') {
            return t('listings.units.squareMeters', { value: formattedNumber });
        }
        if (attribute === 'distance_to_airport' || attribute === 'distance_to_beach' || attribute === 'distance_to_golf' || attribute === 'distance_to_center') {
            return t('listings.units.kilometers', { value: formattedNumber });
        }

        return formattedNumber;
    };

    useEffect(() => {
        setValue(isRange ? [from, to] : from);
    }, [from, to, isRange]);

    const handleChange = (newValue) => {
        setValue(newValue);
    };

    const handleAfterChange = (newValue) => {
        refine(isRange ? newValue : [newValue, newValue]);
    };

    let step = 10;
    if (attribute === 'price') {
        step = 50000;
    }
    if (attribute === 'year_built') {
        step = 1;
    }
    if (attribute === 'construction_year_quarter') {
        step = 1;
    }


    return (
        <div className="bg-gray-50 p-5 rounded-lg">
            <fieldset>
                <legend className="block text-sm font-medium text-gray-900">
                    {t(`listings.filters.attributes.${attribute}`)}
                </legend>
                <div className="mt-4 px-2">
                    <Slider
                        range={isRange}
                        min={min}
                        max={max}
                        value={value}
                        onChange={handleChange}
                        onAfterChange={handleAfterChange}
                        disabled={!canRefine}
                        step={step}
                        railStyle={{ backgroundColor: '#E5E7EB' }}
                        trackStyle={[{ backgroundColor: '#000' }]}
                        handleStyle={[
                            { borderColor: '#000', backgroundColor: '#FFFFFF' },
                            isRange && { borderColor: '#000', backgroundColor: '#FFFFFF' }
                        ].filter(Boolean)}
                    />
                    <div className="mt-2 flex justify-between text-xs text-gray-800">
                        {isRange ? (
                            <>
                                <span className="-ml-1">{formatValue(value[0], attribute)}</span>
                                <span>{formatValue(value[1], attribute)}</span>
                            </>
                        ) : (
                            <span className="-ml-1">{formatValue(value, attribute)}</span>
                        )}
                    </div>
                </div>
            </fieldset>
        </div>
    );
}